/**
 * 云端敏感操作订单详情：解析 `history_order/head` 查询参数。
 * 有 server_id 时 id=server_id（不传 start/end）；否则 id=order_head_id 并带列表筛选 start/end。
 */

/** 解析入参：列表行标识 + 时段筛选 */
export type CloudSensitiveHistoryOrderHeadInput = {
    /** 列表行 server_id（优先） */
    serverId?: string | number | null;
    /** 列表行 order_head_id（server_id 空时回退） */
    orderHeadId?: string | number | null;
    /** critical_operations/period 列表筛选开始时间 */
    start: string;
    /** critical_operations/period 列表筛选结束时间 */
    end: string;
};

/** history_order/head 查询参数（不含 string_id） */
export type CloudSensitiveHistoryOrderHeadParams = {
    /** 查询 id：server_id 或 order_head_id */
    id: string;
    /** 仅回退 order_head_id 时携带 */
    start?: string;
    /** 仅回退 order_head_id 时携带 */
    end?: string;
};

/**
 * 判断字符串是否为非空有效标识。
 * @param value 原始值
 */
function hasNonEmptyId(value: string | number | null | undefined): boolean {
    return String(value ?? '').trim() !== '';
}

/**
 * 判断 order_head_id 是否为正数有效值。
 * @param orderHeadId 订单头 ID
 */
function hasValidOrderHeadId(orderHeadId: string | number | null | undefined): boolean {
    if (!hasNonEmptyId(orderHeadId)) {
        return false;
    }
    const n = Number(orderHeadId);
    return !Number.isNaN(n) && n > 0;
}

/**
 * 解析云端敏感操作打开 `history_order/head` 的 query。
 * @param input server_id / order_head_id / 列表筛选时段
 * @returns 可请求的参数；两者皆无效时返回 null
 */
export function resolveCloudSensitiveHistoryOrderHeadParams(
    input: CloudSensitiveHistoryOrderHeadInput
): CloudSensitiveHistoryOrderHeadParams | null {
    /** 优先使用的 server_id（去空白） */
    const serverId = String(input.serverId ?? '').trim();
    if (serverId) {
        return { id: serverId };
    }
    if (!hasValidOrderHeadId(input.orderHeadId)) {
        return null;
    }
    /** 回退 order_head_id 时必须有列表筛选时段 */
    const start = String(input.start ?? '').trim();
    const end = String(input.end ?? '').trim();
    if (!start || !end) {
        return null;
    }
    return {
        id: String(input.orderHeadId).trim(),
        start,
        end,
    };
}

/**
 * 云端敏感操作列表行是否可打开订单详情（有 server_id 或有效 order_head_id）。
 * @param row 列表行字段子集
 */
export function canOpenCloudSensitiveOrderDetail(row: {
    server_id?: string | number | null;
    order_head_id?: string | number | null;
}): boolean {
    return (
        hasNonEmptyId(row.server_id) || hasValidOrderHeadId(row.order_head_id)
    );
}
